import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { useSettings } from '../../context/SettingsContext';
import { API_URL } from '../Constants';

const Home = () => {
  const settings = useSettings();
  const navigate = useNavigate();

  const [stage, setStage] = useState('login'); // 'login' or 'code'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [loginData, setLoginData] = useState({
    phone_number: '',
    email_address: ''
  });

  const [code, setCode] = useState('');

  useEffect(() => {
    // Check if user is already logged in (has valid session key)
    const sessionKey = localStorage.getItem('validator_session_key');
    if (sessionKey) {
      // Could add validation here to check if key is still valid
      // For now, just redirect to validate page
      // navigate('/validate');
    }
  }, [navigate]);

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setMessage('');
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value);
    setError('');
    setMessage('');
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      // Create FormData object
      const formDataToSend = new FormData();
      formDataToSend.append('phone_number', loginData.phone_number);
      formDataToSend.append('email_address', loginData.email_address);

      const response = await axios.post(`${API_URL}validator_login`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setMessage(response.data.message);
        setStage('code');
      } else {
        setError(response.data.error || 'An error occurred. Please try again.');
      }
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      // Create FormData object
      const formDataToSend = new FormData();
      formDataToSend.append('code', code);

      const response = await axios.post(`${API_URL}validator_check_login`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success && response.data.session_key) {
        // Store session key in localStorage with expiration (5 days)
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 5);
        
        localStorage.setItem('validator_session_key', response.data.session_key);
        localStorage.setItem('validator_session_expires', expirationDate.toISOString());
        
        // Redirect to validate page or show success
        // For now, we can redirect to a validate page with a placeholder ID
        // Or show a success message
        setMessage('Login successful! Redirecting...');
        setTimeout(() => {
          // Navigate to validate page - you may need to adjust this based on your routing
          navigate('/validate/new');
        }, 1000);
      } else {
        setError(response.data.error || 'Invalid login code.');
      }
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Invalid login code.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      <nav className="w-full fixed z-50">
        <div className="w-full mx-auto bg-primary lg:py-3 py-5">
          <h1 className='text-white text-center my-0'>Valparx Validation</h1>
        </div>
      </nav>

      <div className='flex items-center justify-center min-h-screen'>
        <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 pt-10'>
          <div className='mb-8 text-center'>
            <h1 className='text-3xl font-bold text-gray-900 mb-4'>
              Validator Login
            </h1>
            <p className='text-gray-600'>
              Validator accounts must be approved before they can validate parking with ValParx.
            </p>
          </div>

          {error && (
            <div className='mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg'>
              {error}
            </div>
          )}

          {message && (
            <div className='mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg'>
              {message}
            </div>
          )}

          {stage === 'login' ? (
            <>
              <div className='mb-6'>
                <p className='text-sm text-gray-600 mb-4'>
                  Please enter the information you used to create your validator account:
                </p>
              </div>

              <form onSubmit={handleLoginSubmit} className='space-y-6'>
                {/* Phone Number */}
                <div>
                  <label htmlFor='phone_number' className='block text-sm font-medium text-gray-700 mb-2'>
                    Phone Number
                  </label>
                  <input
                    type='tel'
                    id='phone_number'
                    name='phone_number'
                    value={loginData.phone_number}
                    onChange={handleLoginInputChange}
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                    placeholder='(555) 123-4567'
                  />
                </div>

                {/* Email Address */}
                <div>
                  <label htmlFor='email_address' className='block text-sm font-medium text-gray-700 mb-2'>
                    Email Address
                  </label>
                  <input
                    type='email'
                    id='email_address'
                    name='email_address'
                    value={loginData.email_address}
                    onChange={handleLoginInputChange}
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                    placeholder='your@email.com'
                  />
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Login Code'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className='mb-6'>
                <p className='text-sm text-gray-600 mb-4'>
                  If the data you provided is valid, a login code will be sent to the information you provided. Please enter it below:
                </p>
              </div>

              <form onSubmit={handleCodeSubmit} className='space-y-6'>
                {/* Login Code */}
                <div>
                  <label htmlFor='code' className='block text-sm font-medium text-gray-700 mb-2'>
                    Login Code
                  </label>
                  <input
                    type='text'
                    id='code'
                    name='code'
                    value={code}
                    onChange={handleCodeChange}
                    maxLength={6}
                    required
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-center text-2xl tracking-widest uppercase font-mono'
                    placeholder='000000'
                  />
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? 'Verifying...' : 'Verify Code'}
                  </button>
                </div>

                {/* Back Button */}
                <div>
                  <button
                    type='button'
                    onClick={() => {
                      setStage('login');
                      setCode('');
                      setError('');
                      setMessage('');
                    }}
                    className='w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-6 rounded-lg transition duration-200'
                  >
                    ← Back to Login
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Signup Link */}
          <div className='mt-8 pt-6 border-t border-gray-200 text-center'>
            <p className='text-gray-600 text-sm'>
              Don't have an account?{' '}
              <Link to='/signup' className='text-blue-600 hover:text-blue-700 font-semibold'>
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default Home;
